// src/pages/Landing.jsx
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

const risks = [
    {
        label: 'Legal exposure',
        desc: 'Detect phrases that create unmanaged liability, structural compliance failures, or breach sensitive contract terms.',
        gradient: 'from-blue-500/10 to-indigo-500/10'
    },
    {
        label: 'Escalation triggers',
        desc: 'Identify passive-aggressive framing, emotional weight, or conflict-provoking language before it creates friction.',
        gradient: 'from-amber-500/10 to-orange-500/10'
    },
    {
        label: 'Tone misalignment',
        desc: 'Calibrate the exact communicative register required for high-stakes B2B relationships and sensitive internal teams.',
        gradient: 'from-purple-500/10 to-pink-500/10'
    },
    {
        label: 'Commitment drift',
        desc: 'Isolate ambiguous formatting or trailing promises that inadvertently surrender leverage or undermine positions.',
        gradient: 'from-emerald-500/10 to-teal-500/10'
    },
]

export default function Landing() {
    const { user } = useAuth()

    return (
        <div className="min-h-screen bg-[#07090e] text-slate-100 font-body antialiased relative overflow-x-hidden selection:bg-brand-amber/30 selection:text-white">

            {/* Premium Ambient Background Glows */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[280px] bg-gradient-to-b from-brand-amber/[0.07] to-transparent blur-[130px] pointer-events-none opacity-45" />
            <div className="absolute top-[600px] right-[-10%] w-[600px] h-[600px] bg-indigo-500/5 blur-[150px] pointer-events-none rounded-full" />

            <Navbar />

            {/* Hero Section */}
            <section className="relative max-w-6xl mx-auto px-6 pt-24 pb-20 md:pt-36 md:pb-32 text-center md:text-left">
                <div className="max-w-4xl relative z-10">
                    {/* High-End Enterprise Pill Banner */}
                    <div className="inline-flex items-center gap-3 bg-white/[0.04] backdrop-blur-md border border-white/[0.12] shadow-[0_1px_12px_rgba(0,0,0,0.5)] rounded-full px-6 py-2.5 mb-10 transition-all duration-300 hover:border-brand-amber/40">
                        <div className="w-2 h-2 rounded-full bg-brand-amber shadow-[0_0_8px_rgba(212,168,67,0.9)]" />
                        <span className="text-xs font-semibold tracking-[0.2em] uppercase text-slate-300">
                            Professional Communication Risk Engine
                        </span>
                    </div>

                    {/* Massive Premium Typography Hierarchy */}
                    <h1 className="relative z-10 text-5xl sm:text-6xl md:text-7xl font-display font-bold tracking-tight text-white leading-[1.08] mb-8 [text-shadow:none] [text-rendering:optimizeLegibility]">
                        Know the risk before<br />
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-brand-amber2 via-brand-amber to-brand-amber2">
                            you press send.
                        </span>
                    </h1>

                    <p className="text-slate-400 text-lg sm:text-xl font-normal leading-relaxed mb-12 max-w-2xl font-body">
                        Guardian Message Check instantly audits your critical outbound communications
                        for unmanaged liabilities, escalation loops, and structural tone vulnerabilities
                        before they reach the wire.
                    </p>

                    {/* Auth Controlled Premium CTAs */}
                    <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-4">
                        {user ? (
                            <>
                                <Link to="/analyze" className="w-full sm:w-auto px-8 py-3.5 bg-brand-amber hover:bg-brand-amber2 text-[#07090e] font-medium rounded-lg shadow-[0_4px_20px_-4px_rgba(212,168,67,0.45)] hover:shadow-[0_6px_26px_-4px_rgba(240,200,102,0.6)] transition-all duration-200 hover:scale-[1.01] text-center">
                                    Continue analysing
                                </Link>
                                <Link to="/plans" className="w-full sm:w-auto px-8 py-3.5 bg-white/[0.02] hover:bg-white/[0.07] text-slate-300 hover:text-white border border-white/[0.08] hover:border-white/25 font-medium rounded-lg transition-all duration-200 hover:scale-[1.01] text-center backdrop-blur-sm">
                                    View plans
                                </Link>
                            </>
                        ) : (
                            <>
                                <Link to="/register" className="w-full sm:w-auto px-8 py-3.5 bg-brand-amber hover:bg-brand-amber2 text-[#07090e] font-medium rounded-lg shadow-[0_4px_20px_-4px_rgba(212,168,67,0.45)] hover:shadow-[0_6px_26px_-4px_rgba(240,200,102,0.6)] transition-all duration-200 hover:scale-[1.01] text-center">
                                    Start analysing free
                                </Link>
                                <Link to="/login" className="w-full sm:w-auto px-8 py-3.5 bg-white/[0.02] hover:bg-white/[0.07] text-slate-300 hover:text-white border border-white/[0.08] hover:border-white/25 font-medium rounded-lg transition-all duration-200 hover:scale-[1.01] text-center backdrop-blur-sm">
                                    Sign in to account
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            </section>

            {/* Risk Categories — Grid Redesign using Layered Shadows & Clean Gradients */}
            <section className="max-w-6xl mx-auto px-6 py-20 relative">
                <div className="mb-12">
                    <p className="text-brand-amber text-[11px] font-medium tracking-[0.25em] uppercase mb-3">
                        Detection Taxonomy
                    </p>
                    <h2 className="text-2xl md:text-3xl font-display font-semibold tracking-tight text-white">
                        Engineered for zero communication slippage.
                    </h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {risks.map((r) => (
                        <div
                            key={r.label}
                            className="group relative bg-white/[0.01] backdrop-blur-md border border-white/[0.05] rounded-xl p-6 transition-all duration-300 hover:bg-white/[0.03] hover:border-white/[0.1] hover:shadow-[0_10px_30px_rgba(0,0,0,0.4)] hover:-translate-y-0.5"
                        >
                            {/* Micro Ambient Glow behind icon */}
                            <div className={`absolute top-6 left-6 w-8 h-8 rounded-md bg-gradient-to-br ${r.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm pointer-events-none`} />

                            {/* Minimalist Tech Dot Marker */}
                            <div className="relative w-7 h-7 rounded-md bg-white/[0.03] border border-white/[0.08] flex items-center justify-center mb-6 transition-colors group-hover:border-brand-amber/40">
                                <div className="w-1.5 h-1.5 rounded-full bg-slate-500 transition-colors group-hover:bg-brand-amber" />
                            </div>

                            <h3 className="font-display text-white text-base font-medium tracking-tight mb-2">
                                {r.label}
                            </h3>
                            <p className="text-slate-400 text-sm leading-relaxed font-light">
                                {r.desc}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Premium Zero-Knowledge Trust Architecture Section */}
            <section className="max-w-6xl mx-auto px-6 py-16 md:py-24 relative">
                <div className="relative bg-gradient-to-b from-white/[0.02] to-transparent border border-white/[0.04] rounded-2xl p-8 md:p-12 overflow-hidden flex flex-col md:flex-row items-start md:items-center justify-between gap-8 backdrop-blur-md">
                    <div className="absolute inset-0 bg-radial-gradient from-brand-amber/[0.01] to-transparent opacity-50 pointer-events-none" />

                    <div className="max-w-xl relative z-10">
                        <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-3 py-0.5 mb-4">
                            <span className="text-[10px] text-emerald-400 font-medium tracking-wide uppercase">
                                Privacy Protocol
                            </span>
                        </div>
                        <h3 className="text-2xl md:text-3xl font-display font-semibold tracking-tight text-white mb-4">
                            Zero-knowledge ephemeral isolation.
                        </h3>
                        <p className="text-slate-400 text-sm md:text-base leading-relaxed font-light">
                            Guardian Message Check operates under a strict stateless paradigm. Your analysis requests
                            are processed exclusively in-memory and immediately destroyed following compilation.
                            We retain absolute zero transaction logs, zero message histories, and zero metadata footprints.
                        </p>
                    </div>

                    <div className="flex items-center gap-6 md:self-center bg-white/[0.02] border border-white/[0.06] rounded-xl px-6 py-5 relative z-10 w-full md:w-auto shadow-[0_4px_24px_rgba(0,0,0,0.3)]">
                        <div className="flex flex-col">
                            <span className="text-2xl font-mono font-bold tracking-tight text-white">0.00%</span>
                            <span className="text-slate-500 text-[11px] uppercase tracking-wider mt-0.5">Data Retention Rate</span>
                        </div>
                        <div className="w-px h-10 bg-white/[0.08]" />
                        <div className="flex flex-col">
                            <span className="text-xs font-mono text-emerald-400 font-semibold uppercase tracking-widest bg-emerald-500/5 px-2 py-0.5 rounded border border-emerald-500/10">
                                Active
                            </span>
                            <span className="text-slate-500 text-[10px] tracking-wide mt-1">End-to-End Stateless</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Sleek Bottom CTA Dashboard Hook */}
            <section className="relative max-w-6xl mx-auto px-6 pb-24 pt-8">
                <div className="w-full bg-gradient-to-r from-white/[0.01] via-white/[0.02] to-white/[0.01] border-y border-white/[0.04] py-12 md:py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
                    <div>
                        <h2 className="font-display text-2xl text-white font-medium tracking-tight mb-2">
                            {user ? 'Ready to analyze another message?' : 'Secure your outbound communications.'}
                        </h2>
                        <p className="text-slate-400 font-body text-sm">
                            {user ? 'Return directly to the professional risk assessment engine.' : 'Run on-demand structural diagnostics or subscribe to pipeline scaling plans.'}
                        </p>
                    </div>
                    <Link
                        to={user ? '/analyze' : '/register'}
                        className="w-full md:w-auto px-6 py-3 bg-white text-[#07090e] hover:bg-slate-200 font-medium rounded-lg transition-all duration-200 shadow-xl text-center shrink-0"
                    >
                        {user ? 'Open Risk Dashboard' : 'Create Free Account'}
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    )
}